import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSession } from "../auth/useSession";
import { useCreator } from "../creator/CreatorContext";
import { TierBadge } from "../design/primitives";
import logo from "../img/tvog.png";
import css from "./header.module.css";

/**
 * §7 Header - the app's **only** chrome row, on both surfaces.
 *
 * The handoff is explicit that there is one bar: "logo + `STUDIO` in maroon
 * mono, nav, `ADAM · OWNER` right" (navigation handoff, Turn 1 model 1b).
 * Studio used to draw its own nav in a second row inside the page, which put
 * two competing rows of nav items on screen at once and left the wordmark and
 * the nav at different heights depending on which surface you were on. Now the
 * wordmark holds one position and the word in it swaps; the nav holds one
 * position and its *items* swap.
 *
 * Nav is **data, not markup** (Q-6) - a link that should not exist is absent
 * from the array, not commented out in JSX. `REQUESTS` does not render.
 */

export type HeaderNavItem = {
  to: string;
  label: string;
  /**
   * Computed by the caller, never by the header.
   *
   * `NavLink`'s `isActive` cannot express Studio's rule - a child route marks
   * its *parent*, so `/studio/assets/new` has to light `ASSETS` - and prefix
   * matching cannot either, because `/studio` is a prefix of every Studio
   * route. It is a property of the route tree, so the route tree answers it
   * (`studioChrome`) and the header only draws the answer.
   */
  active: boolean;
};

/**
 * Patron nav — `TABS · ACCOUNT`.
 *
 * Active **by prefix**, which is `tab-return-row` R-2: `TABS` stays lit for the
 * whole time a tab is open, including `/tabs/lost-woods`. The nav names the
 * section, not the screen — depth is the return row's job, one row down.
 */
const usePatronNav = (): HeaderNavItem[] => {
  const { pathname } = useLocation();
  return [
    {
      to: "/tabs",
      label: "TABS",
      active: pathname === "/tabs" || pathname.startsWith("/tabs/"),
    },
    // The avatar also reaches /account (§7, Q-6). Both, deliberately: the nav
    // item is the labelled route for anyone not reading a 38px square as a
    // link.
    { to: "/account", label: "ACCOUNT", active: pathname === "/account" },
  ];
};

export function Header({
  wordmark = "PERKS",
  nav,
  navLabel = "Main",
  onNavigate,
  right,
}: {
  /** §11: the wordmark label is the only word that changes. Maroon in Studio. */
  wordmark?: "PERKS" | "STUDIO";
  /** Omitted on the patron surface, which derives its own from the URL. */
  nav?: HeaderNavItem[];
  navLabel?: string;
  /**
   * Studio routes its nav clicks through the unsaved-changes guard. Anchors
   * either way, so middle-click and open-in-new-tab keep working.
   */
  onNavigate?: (to: string) => void;
  /** Replaces the tier badge + avatar. Studio prints `ADAM · OWNER` here. */
  right?: ReactNode;
}) {
  const { me } = useCreator();
  const session = useSession();
  const patronNav = usePatronNav();
  const items = nav ?? patronNav;

  const displayName = session.name ?? me?.name ?? "";
  const initials = displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <header className={css.header}>
      <Link to="/" className={css.brand}>
        {/* §5: the logo PNG is the one real <img>, and the one asset rendered
            with image-rendering:auto. Header lockup only, at rest - never
            stretched, animated, recoloured or used as decoration. */}
        <img src={logo} alt="" className={css.logo} />
        <span className={wordmark === "STUDIO" ? css.wordmarkStudio : css.wordmark}>
          {wordmark}
        </span>
      </Link>

      <nav className={css.nav} aria-label={navLabel}>
        {items.map((item) => (
          <a
            key={item.to}
            href={item.to}
            aria-current={item.active ? "page" : undefined}
            className={item.active ? `${css.navItem} ${css.navItemActive}` : css.navItem}
            onClick={(e) => {
              // A modified click opens a new tab and abandons nothing, so
              // there is nothing for the guard to ask about.
              if (!onNavigate) return;
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              onNavigate(item.to);
            }}
          >
            {item.label}
          </a>
        ))}
      </nav>

      <div className={css.right}>
        {right ?? (
          <>
            {me?.tier && <TierBadge name={me.tier.name} />}
            {/* §7 / Q-6: the avatar links to /account. There is no dropdown. */}
            <Link to="/account" className={css.avatar} aria-label="Your account">
              {initials}
            </Link>
          </>
        )}
      </div>
    </header>
  );
}
